// src/components/DocumentUpload/DocumentUpload.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  LinearProgress,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Collapse,
  Fade,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  alpha,
  CircularProgress,
  Divider,
  Stack,
  FormHelperText,AlertTitle
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  PictureAsPdf,
  Image,
  Delete,
  RefreshOutlined,
  VerifiedOutlined,
  Warning,
  Info,
  Schedule,
  Cancel,
  AttachFile,
  FilePresent,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';

// ============================================
// BRAND COLORS
// ============================================

const BRAND_COLORS = {
  primary: '#2E7D32',
  primaryLight: '#4CAF50',
  primaryDark: '#1B5E20',
  primaryPale: '#E8F5E9',
  secondary: '#C62828',
  accent: '#FF6F00',
  background: '#F5F7FA',
  surface: '#FFFFFF',
  text: '#1A1A1A',
  textSecondary: '#666666',
  border: '#E8ECF1',
  success: '#2E7D32',
  warning: '#ED6C02',
  error: '#D32F2F',
  info: '#0288D1',
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[DOC-UPLOAD] ℹ️ ${message}`, data || ''),
  error: (message, error) => console.error(`[DOC-UPLOAD] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[DOC-UPLOAD] 🐛 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[DOC-UPLOAD] ⚠️ ${message}`, data || ''),
  success: (message, data) => console.log(`[DOC-UPLOAD] ✅ ${message}`, data || ''),
};

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const API_ENDPOINTS = {
  UPLOAD_DOCUMENT: `${API_BASE_URL}/suppliers/upload_supplier_document.php`,
  GET_DOCUMENTS: `${API_BASE_URL}/suppliers/get_supplier_documents.php`,
  DELETE_DOCUMENT: `${API_BASE_URL}/suppliers/delete_supplier_document.php`,
};

// ============================================
// DOCUMENT TYPES CONFIGURATION
// ============================================

export const DOCUMENT_TYPES = {
  cac_certificate: {
    label: 'Chemical Certificate',
    description: 'Certification for chemical dealers and manufacturers',
    icon: <Description />,
    required: true,
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['pdf', 'jpg', 'jpeg', 'png'],
    allowedMimeTypes: ['application/pdf', 'image/jpeg', 'image/png'],
  },
  // Additional document types can be added here
  // tax_clearance: {
  //   label: 'Tax Clearance Certificate',
  //   description: 'Valid Tax Clearance Certificate',
  //   icon: <Description />,
  //   required: true,
  //   maxSize: 5 * 1024 * 1024,
  //   allowedTypes: ['pdf', 'jpg', 'jpeg', 'png'],
  //   allowedMimeTypes: ['application/pdf', 'image/jpeg', 'image/png'],
  // },
};

// ============================================
// DOCUMENT UPLOAD SERVICE
// ============================================

const documentService = {
  /**
   * Upload a document for a supplier
   * @param {number} userId - The user ID
   * @param {string} docType - The document type (e.g., 'cac_certificate')
   * @param {File} file - The file to upload
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} - Upload result
   */
  async uploadDocument(userId, docType, file, onProgress) {
    try {
      logger.info(`Uploading document: ${docType} for user ${userId}`);
      
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append(docType, file);

      const response = await axios.post(
        API_ENDPOINTS.UPLOAD_DOCUMENT,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted);
            }
          },
          timeout: 60000,
        }
      );

      if (response.data.success) {
        logger.success(`Document uploaded successfully: ${docType}`);
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message || 'Document uploaded successfully'
        };
      } else {
        logger.error(`Upload failed: ${response.data.message}`);
        return { 
          success: false, 
          error: response.data.message || 'Upload failed',
          errors: response.data.errors
        };
      }
    } catch (error) {
      logger.error(`Upload error for ${docType}:`, error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Upload failed'
      };
    }
  },

  /**
   * Get all documents for a supplier
   * @param {number} userId - The user ID
   * @returns {Promise} - Documents list
   */
  async getDocuments(userId) {
    try {
      logger.debug(`Fetching documents for user ${userId}`);
      const response = await axios.get(
        API_ENDPOINTS.GET_DOCUMENTS,
        { 
          params: { user_id: userId },
          headers: { 'Accept': 'application/json' },
          timeout: 15000,
        }
      );
      
      if (response.data.success) {
        logger.success(`Fetched ${response.data.data.length} documents`);
        return { success: true, data: response.data.data };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      logger.error('Failed to fetch documents:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a document
   * @param {number} documentId - The document ID
   * @returns {Promise} - Delete result
   */
  async deleteDocument(documentId) {
    try {
      logger.debug(`Deleting document ${documentId}`);
      const response = await axios.delete(
        `${API_ENDPOINTS.DELETE_DOCUMENT}?id=${documentId}`,
        {
          headers: { 'Accept': 'application/json' },
          timeout: 15000,
        }
      );
      
      if (response.data.success) {
        logger.success(`Document ${documentId} deleted`);
        return { success: true, message: response.data.message };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      logger.error('Failed to delete document:', error);
      return { success: false, error: error.message };
    }
  },
};

// ============================================
// DOCUMENT UPLOAD COMPONENT
// ============================================

/**
 * DocumentUpload Component
 * 
 * A standalone document upload component that can be used anywhere in the app.
 * Supports drag-and-drop, file validation, progress tracking, and document management.
 * 
 * @param {Object} props
 * @param {number} props.userId - The user ID (required)
 * @param {string} props.userType - The user type (e.g., 'supplier') - defaults to 'supplier'
 * @param {string} props.uploadEndpoint - Custom upload endpoint (optional)
 * @param {string} props.getEndpoint - Custom get endpoint (optional)
 * @param {string} props.deleteEndpoint - Custom delete endpoint (optional)
 * @param {Object} props.documentTypes - Custom document types configuration (optional)
 * @param {Function} props.onUploadComplete - Callback when upload completes
 * @param {Function} props.onDocumentChange - Callback when documents change
 * @param {Function} props.onError - Callback when error occurs
 * @param {boolean} props.readOnly - If true, hides upload controls
 * @param {boolean} props.showUploadArea - Show upload area for new documents
 * @param {boolean} props.autoLoad - Auto-load documents on mount
 * @param {Function} props.renderCustomStatus - Custom status renderer
 * @param {Object} props.sx - Additional styles
 */
const DocumentUpload = ({
  userId,
  userType = 'supplier',
  uploadEndpoint,
  getEndpoint,
  deleteEndpoint,
  documentTypes = DOCUMENT_TYPES,
  onUploadComplete,
  onDocumentChange,
  onError,
  readOnly = false,
  showUploadArea = true,
  autoLoad = true,
  renderCustomStatus,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentStatus, setDocumentStatus] = useState({});
  const [documentProgress, setDocumentProgress] = useState({});
  const [documentErrors, setDocumentErrors] = useState({});
  const [uploadComplete, setUploadComplete] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Refs for file inputs
  const fileInputRefs = useRef({});
  const dropZoneRef = useRef(null);

  // ============================================
  // VALIDATION HELPER
  // ============================================

  const validateFile = useCallback((docType, file) => {
    const config = documentTypes[docType];
    if (!config) {
      return { valid: false, error: `Unknown document type: ${docType}` };
    }

    // Check file size
    if (file.size > config.maxSize) {
      const maxSizeMB = config.maxSize / (1024 * 1024);
      return { 
        valid: false, 
        error: `File size exceeds ${maxSizeMB}MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB` 
      };
    }

    // Check file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!config.allowedTypes.includes(fileExtension)) {
      return { 
        valid: false, 
        error: `Only ${config.allowedTypes.join(', ')} files are allowed. Received: .${fileExtension}` 
      };
    }

    return { valid: true, error: null };
  }, [documentTypes]);

  // ============================================
  // LOAD DOCUMENTS
  // ============================================

  const loadDocuments = useCallback(async () => {
    if (!userId) {
      logger.warn('No userId provided for document loading');
      return;
    }

    setLoadingDocuments(true);
    try {
      const endpoint = getEndpoint || API_ENDPOINTS.GET_DOCUMENTS;
      const result = await documentService.getDocuments(userId);
      
      if (result.success) {
        setDocuments(result.data);
        
        // Update document status
        const status = {};
        result.data.forEach(doc => {
          status[doc.document_type] = {
            status: doc.status,
            filePath: doc.file_path,
            fileName: doc.original_name,
            fileSize: doc.file_size_formatted,
            id: doc.id,
          };
        });
        setDocumentStatus(status);
        
        // Check if all required documents are uploaded
        const requiredTypes = Object.keys(documentTypes).filter(
          key => documentTypes[key].required
        );
        const allUploaded = requiredTypes.every(
          type => status[type]?.status === 'uploaded' || 
                  status[type]?.status === 'verified' ||
                  result.data.some(doc => doc.document_type === type && doc.status !== 'rejected')
        );
        setUploadComplete(allUploaded);
        
        logger.success(`Loaded ${result.data.length} documents`);
        if (onDocumentChange) {
          onDocumentChange(result.data);
        }
      } else {
        logger.error('Failed to load documents:', result.error);
        if (onError) {
          onError(result.error);
        }
        setSnackbar({
          open: true,
          message: result.error || 'Failed to load documents',
          severity: 'error',
        });
      }
    } catch (error) {
      logger.error('Error loading documents:', error);
      if (onError) {
        onError(error.message);
      }
    } finally {
      setLoadingDocuments(false);
    }
  }, [userId, getEndpoint, documentTypes, onDocumentChange, onError]);

  // ============================================
  // UPLOAD DOCUMENT
  // ============================================

  const handleUpload = useCallback(async (docType, file) => {
    if (!userId) {
      setSnackbar({
        open: true,
        message: 'User ID not found. Please complete registration first.',
        severity: 'error',
      });
      return;
    }

    // Validate file
    const validation = validateFile(docType, file);
    if (!validation.valid) {
      setDocumentErrors(prev => ({
        ...prev,
        [docType]: validation.error,
      }));
      setSnackbar({
        open: true,
        message: validation.error,
        severity: 'error',
      });
      return;
    }

    setUploading(true);
    setDocumentProgress(prev => ({ ...prev, [docType]: 0 }));
    setDocumentErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[docType];
      return newErrors;
    });

    try {
      const endpoint = uploadEndpoint || API_ENDPOINTS.UPLOAD_DOCUMENT;
      const result = await documentService.uploadDocument(
        userId,
        docType,
        file,
        (progress) => {
          setDocumentProgress(prev => ({ ...prev, [docType]: progress }));
        }
      );

      if (result.success) {
        setDocumentStatus(prev => ({
          ...prev,
          [docType]: {
            status: 'uploaded',
            filePath: result.data?.uploaded?.[docType]?.file_path,
            fileName: file.name,
            fileSize: formatFileSize(file.size),
          }
        }));
        
        setSnackbar({
          open: true,
          message: `${documentTypes[docType]?.label || docType} uploaded successfully!`,
          severity: 'success',
        });
        
        // Reload documents
        await loadDocuments();
        
        if (onUploadComplete) {
          onUploadComplete(docType, result.data);
        }
      } else {
        const errorMessage = result.errors?.[docType] || result.error || 'Upload failed';
        setDocumentErrors(prev => ({
          ...prev,
          [docType]: errorMessage,
        }));
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload document';
      setDocumentErrors(prev => ({
        ...prev,
        [docType]: errorMessage,
      }));
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setUploading(false);
      setDocumentProgress(prev => ({ ...prev, [docType]: 0 }));
    }
  }, [userId, uploadEndpoint, documentTypes, loadDocuments, onUploadComplete, onError, validateFile]);

  // ============================================
  // DELETE DOCUMENT
  // ============================================

  const handleDelete = useCallback(async () => {
    if (!selectedDocument) return;

    try {
      const result = await documentService.deleteDocument(selectedDocument.id);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Document deleted successfully',
          severity: 'success',
        });
        await loadDocuments();
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to delete document',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete document',
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    }
  }, [selectedDocument, loadDocuments]);

  // ============================================
  // FILE HANDLERS
  // ============================================

  const handleFileSelect = useCallback((docType, event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(docType, file);
    }
    // Reset input
    event.target.value = '';
  }, [handleUpload]);

  const handleDrop = useCallback((docType, event) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleUpload(docType, file);
    }
  }, [handleUpload]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  const getFileIcon = useCallback((fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <PictureAsPdf sx={{ color: BRAND_COLORS.error }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image sx={{ color: BRAND_COLORS.info }} />;
      default:
        return <Description sx={{ color: BRAND_COLORS.textSecondary }} />;
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'verified':
        return <VerifiedOutlined sx={{ color: BRAND_COLORS.success }} />;
      case 'uploaded':
        return <CheckCircle sx={{ color: BRAND_COLORS.success }} />;
      case 'pending':
        return <Schedule sx={{ color: BRAND_COLORS.warning }} />;
      case 'rejected':
        return <Cancel sx={{ color: BRAND_COLORS.error }} />;
      default:
        return <Info sx={{ color: BRAND_COLORS.info }} />;
    }
  }, []);

  const getStatusLabel = useCallback((status) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'uploaded':
        return 'Uploaded';
      case 'pending':
        return 'Pending Review';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'verified':
        return BRAND_COLORS.success;
      case 'uploaded':
        return BRAND_COLORS.success;
      case 'pending':
        return BRAND_COLORS.warning;
      case 'rejected':
        return BRAND_COLORS.error;
      default:
        return BRAND_COLORS.info;
    }
  }, []);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (autoLoad && userId) {
      loadDocuments();
    }
  }, [autoLoad, userId, loadDocuments]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderDocumentCard = useCallback((docType, config) => {
    const status = documentStatus[docType];
    const existing = documents.find(d => d.document_type === docType);
    const isUploaded = status?.status === 'uploaded' || 
                       status?.status === 'verified' ||
                       (existing && existing.status === 'verified');
    const isPending = existing && existing.status === 'pending';
    const isRejected = existing && existing.status === 'rejected';
    const hasError = !!documentErrors[docType];
    const progress = documentProgress[docType] || 0;
    const isUploading = progress > 0 && progress < 100;

    // If custom renderer provided, use it
    if (renderCustomStatus) {
      const customRender = renderCustomStatus({
        docType,
        config,
        status,
        existing,
        isUploaded,
        isPending,
        isRejected,
        hasError,
        progress,
        isUploading,
      });
      if (customRender) return customRender;
    }

    return (
      <Card
        key={docType}
        sx={{
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${
            hasError ? BRAND_COLORS.error :
            isRejected ? BRAND_COLORS.error :
            isPending ? BRAND_COLORS.warning :
            isUploaded ? BRAND_COLORS.success :
            BRAND_COLORS.border
          }`,
          backgroundColor: hasError ? alpha(BRAND_COLORS.error, 0.02) :
                           isRejected ? alpha(BRAND_COLORS.error, 0.02) :
                           isPending ? alpha(BRAND_COLORS.warning, 0.02) :
                           isUploaded ? alpha(BRAND_COLORS.success, 0.02) :
                           BRAND_COLORS.surface,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha(BRAND_COLORS.primary, 0.08)}`,
          },
          ...(dragOver && {
            borderColor: BRAND_COLORS.primary,
            borderWidth: 2,
            backgroundColor: alpha(BRAND_COLORS.primary, 0.04),
          }),
        }}
        onDragOver={(e) => handleDragOver(e)}
        onDragLeave={(e) => handleDragLeave(e)}
        onDrop={(e) => handleDrop(docType, e)}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
              <Avatar
                sx={{
                  bgcolor: isPending ? BRAND_COLORS.warning : 
                           isRejected ? BRAND_COLORS.error :
                           isUploaded ? BRAND_COLORS.success :
                           BRAND_COLORS.primaryPale,
                  color: isPending ? '#fff' :
                         isRejected ? '#fff' :
                         isUploaded ? '#fff' :
                         BRAND_COLORS.primary,
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  flexShrink: 0,
                }}
              >
                {config.icon || <Description />}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {config.label}
                  </Typography>
                  {config.required && (
                    <Chip
                      label="Required"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        bgcolor: alpha(BRAND_COLORS.error, 0.08),
                        color: BRAND_COLORS.error,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: BRAND_COLORS.textSecondary, display: 'block' }}>
                  {config.description}
                </Typography>
                
                {/* Status chips */}
                {isUploaded && (
                  <Chip
                    icon={getStatusIcon('uploaded')}
                    label={getStatusLabel('uploaded')}
                    size="small"
                    sx={{
                      mt: 1,
                      bgcolor: alpha(BRAND_COLORS.success, 0.12),
                      color: BRAND_COLORS.success,
                      fontWeight: 600,
                      height: 24,
                      '& .MuiChip-icon': { color: BRAND_COLORS.success },
                    }}
                  />
                )}
                {isPending && (
                  <Chip
                    icon={getStatusIcon('pending')}
                    label={getStatusLabel('pending')}
                    size="small"
                    sx={{
                      mt: 1,
                      bgcolor: alpha(BRAND_COLORS.warning, 0.12),
                      color: BRAND_COLORS.warning,
                      fontWeight: 600,
                      height: 24,
                      '& .MuiChip-icon': { color: BRAND_COLORS.warning },
                    }}
                  />
                )}
                {isRejected && (
                  <Chip
                    icon={getStatusIcon('rejected')}
                    label={getStatusLabel('rejected')}
                    size="small"
                    sx={{
                      mt: 1,
                      bgcolor: alpha(BRAND_COLORS.error, 0.12),
                      color: BRAND_COLORS.error,
                      fontWeight: 600,
                      height: 24,
                      '& .MuiChip-icon': { color: BRAND_COLORS.error },
                    }}
                  />
                )}
                {isRejected && existing?.rejection_reason && (
                  <Alert severity="error" sx={{ mt: 1, borderRadius: 2, p: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Rejection reason: {existing.rejection_reason}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Box>
            
            {/* Actions */}
            {!readOnly && isUploaded && (
              <Tooltip title="Delete document">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedDocument(existing || status);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ color: BRAND_COLORS.error, flexShrink: 0 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* File info when uploaded */}
          {isUploaded && (status?.fileName || existing?.original_name) && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {getFileIcon(status?.fileName || existing?.original_name)}
              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, wordBreak: 'break-all' }}>
                {status?.fileName || existing?.original_name}
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND_COLORS.textSecondary }}>
                {status?.fileSize || existing?.file_size_formatted || 'Uploaded'}
              </Typography>
              {status?.status === 'verified' && (
                <Chip
                  label="Verified"
                  size="small"
                  sx={{
                    bgcolor: BRAND_COLORS.success,
                    color: '#fff',
                    fontWeight: 600,
                    height: 20,
                    fontSize: '0.6rem',
                  }}
                />
              )}
            </Box>
          )}

          {/* Upload progress */}
          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  borderRadius: 2, 
                  height: 6,
                  bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: BRAND_COLORS.primary,
                    borderRadius: 2,
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: BRAND_COLORS.textSecondary, mt: 0.5, display: 'block', textAlign: 'center' }}>
                Uploading... {progress}%
              </Typography>
            </Box>
          )}

          {/* Error message */}
          {hasError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, p: 1 }}>
              <Typography variant="body2">{documentErrors[docType]}</Typography>
            </Alert>
          )}

          {/* Upload area - only if not uploaded and not read-only */}
          {!isUploaded && !readOnly && showUploadArea && (
            <Box
              sx={{
                mt: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 2,
                border: `2px dashed ${hasError ? BRAND_COLORS.error : dragOver ? BRAND_COLORS.primary : BRAND_COLORS.border}`,
                backgroundColor: hasError ? alpha(BRAND_COLORS.error, 0.02) :
                                 dragOver ? alpha(BRAND_COLORS.primary, 0.04) :
                                 'transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: BRAND_COLORS.primary,
                  backgroundColor: alpha(BRAND_COLORS.primary, 0.04),
                },
              }}
              onClick={() => fileInputRefs.current[docType]?.click()}
              onDragOver={(e) => handleDragOver(e)}
              onDragLeave={(e) => handleDragLeave(e)}
              onDrop={(e) => handleDrop(docType, e)}
            >
              <input
                ref={el => fileInputRefs.current[docType] = el}
                type="file"
                accept={config.allowedTypes.map(type => `.${type}`).join(',')}
                onChange={(e) => handleFileSelect(docType, e)}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <CloudUpload sx={{ 
                fontSize: 40, 
                color: hasError ? BRAND_COLORS.error : BRAND_COLORS.primary, 
                mb: 1,
                opacity: dragOver ? 1 : 0.7,
              }} />
              <Typography variant="body1" sx={{ fontWeight: 600, color: BRAND_COLORS.text }}>
                {dragOver ? 'Drop your file here' : `Upload ${config.label}`}
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND_COLORS.textSecondary, display: 'block', mt: 0.5 }}>
                or click to browse files
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND_COLORS.textSecondary, display: 'block', mt: 1 }}>
                Supported formats: {config.allowedTypes.join(', ')} • Max size: {config.maxSize / (1024 * 1024)}MB
              </Typography>
            </Box>
          )}

          {/* Pending state message */}
          {isPending && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} sx={{ color: BRAND_COLORS.warning }} />
              <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mt: 1 }}>
                Your document is being reviewed by our team.
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND_COLORS.textSecondary }}>
                This usually takes 1-2 business days.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }, [
    documents,
    documentStatus,
    documentErrors,
    documentProgress,
    renderCustomStatus,
    readOnly,
    showUploadArea,
    uploadComplete,
    uploading,
    dragOver,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    getFileIcon,
    getStatusIcon,
    getStatusLabel,
    formatFileSize,
  ]);

  // ============================================
  // MAIN RENDER
  // ============================================

  if (!userId) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        <AlertTitle>User ID Required</AlertTitle>
        Please provide a valid user ID to upload documents.
      </Alert>
    );
  }

  return (
    <Box sx={{ ...sx }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND_COLORS.text }}>
          Document Upload
        </Typography>
        <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary }}>
          Upload required documents for verification. All documents are securely stored.
        </Typography>
      </Box>

      {/* Loading state */}
      {loadingDocuments && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2, color: BRAND_COLORS.textSecondary }}>
            Loading documents...
          </Typography>
        </Box>
      )}

      {/* Document cards */}
      {!loadingDocuments && (
        <Box>
          {Object.entries(documentTypes).map(([docType, config]) => (
            <Fade key={docType} in={true} timeout={300}>
              <Box>
                {renderDocumentCard(docType, config)}
              </Box>
            </Fade>
          ))}
        </Box>
      )}

      {/* Upload complete status */}
      {uploadComplete && !loadingDocuments && (
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ 
            mt: 2, 
            borderRadius: 2,
            border: `1px solid ${alpha(BRAND_COLORS.success, 0.2)}`,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            All required documents uploaded successfully!
          </Typography>
          <Typography variant="body2">
            Your documents are being reviewed. You'll receive a notification once verified.
          </Typography>
        </Alert>
      )}

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 20,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: alpha(BRAND_COLORS.error, 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Delete sx={{ color: BRAND_COLORS.error, fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Delete Document
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="textSecondary">
            Are you sure you want to delete this document?
          </Typography>
          {selectedDocument && (
            <Alert severity="warning" sx={{ borderRadius: 2, mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {selectedDocument.fileName || selectedDocument.original_name || 'Document'}
              </Typography>
            </Alert>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================
// HOOK: useDocuments
// ============================================

/**
 * Hook for managing documents in a component
 * @param {number} userId - The user ID
 * @param {Object} options - Options
 * @returns {Object} - Document management functions and state
 */
export const useDocuments = (userId, options = {}) => {
  const {
    autoLoad = true,
    documentTypes = DOCUMENT_TYPES,
  } = options;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({});
  const [uploadComplete, setUploadComplete] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const result = await documentService.getDocuments(userId);
      if (result.success) {
        setDocuments(result.data);
        
        const statusMap = {};
        result.data.forEach(doc => {
          statusMap[doc.document_type] = {
            status: doc.status,
            filePath: doc.file_path,
            fileName: doc.original_name,
            fileSize: doc.file_size_formatted,
            id: doc.id,
          };
        });
        setStatus(statusMap);
        
        const requiredTypes = Object.keys(documentTypes).filter(
          key => documentTypes[key].required
        );
        const allUploaded = requiredTypes.every(
          type => statusMap[type]?.status === 'uploaded' || 
                  statusMap[type]?.status === 'verified' ||
                  result.data.some(doc => doc.document_type === type && doc.status !== 'rejected')
        );
        setUploadComplete(allUploaded);
        
        return result.data;
      }
    } catch (error) {
      logger.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, documentTypes]);

  const uploadDocument = useCallback(async (docType, file) => {
    if (!userId) {
      throw new Error('User ID not found');
    }

    setUploading(true);
    setProgress(prev => ({ ...prev, [docType]: 0 }));

    try {
      const result = await documentService.uploadDocument(
        userId,
        docType,
        file,
        (progressValue) => {
          setProgress(prev => ({ ...prev, [docType]: progressValue }));
        }
      );

      if (result.success) {
        // Reload documents after successful upload
        await loadDocuments();
        return result;
      } else {
        setErrors(prev => ({
          ...prev,
          [docType]: result.error || 'Upload failed',
        }));
        throw new Error(result.error || 'Upload failed');
      }
    } finally {
      setUploading(false);
      setProgress(prev => ({ ...prev, [docType]: 0 }));
    }
  }, [userId, loadDocuments]);

  const deleteDocument = useCallback(async (documentId) => {
    if (!documentId) {
      throw new Error('Document ID required');
    }

    try {
      const result = await documentService.deleteDocument(documentId);
      if (result.success) {
        await loadDocuments();
        return result;
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      throw error;
    }
  }, [loadDocuments]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && userId) {
      loadDocuments();
    }
  }, [autoLoad, userId, loadDocuments]);

  return {
    documents,
    status,
    loading,
    uploading,
    progress,
    errors,
    uploadComplete,
    loadDocuments,
    uploadDocument,
    deleteDocument,
    setErrors,
  };
};

export default DocumentUpload;